import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Styled from "./styles";
import { useLogout } from "../../services/mutations";
import { isUserLoggedIn } from "../../utils/helpers";
import { ASSETS, PATH } from "../../utils/config";
import {
  getGenericWheelSegments,
  GENERIC_WHEEL_USER_ID,
} from "../../constants/genericWheel";
import { logout } from "../../redux/actions";
import { purgePersistedState } from "../../redux";
import { TextView } from "@components";
import grid from "@assets/images/grid.svg";
import log_out from "@assets/images/log_out.svg";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import LoginIcon from "@mui/icons-material/Login";
import HouseLineArt from "../../assets/icons/HouseLineArt";
import { useLocation } from "react-router-dom";
import {
  resetGallery,
  selectThemesPopup,
  closeThemesPopup,
} from "../../redux/reducers/gallery";
import { clearGalleryPopup } from "../../redux/reducers/galleryPopup";
import { getLoggedInUser } from "../../utils/helpers";
import { useQueryClient } from "react-query";
import { getHasUnsavedChanges } from "../../redux/selectors/segmentBuilderSelectors";
import { clearAllAiWheels } from "../../redux/reducers/aiWheels";
import WheelComponent from "../WheelComponent";
import { useIfUserEligible } from "../../hooks/useIfUserEligible";
import { FEATURES } from "../../constants/features";
import AccountSwitcherSection from "./AccountSwitcherSection";
import { ACCOUNT_SWITCHER_ENABLED } from "./accountSwitcherFlag";
import { removeAccount } from "../../services/accountRegistry";
import { getLoggedInUserToken } from "../../utils/helpers";
import AvatarLinkedWheelsSection from "./AvatarLinkedWheelsSection";
import ThemesPopup from "./ThemesPopup";
import { TEAMS_ENABLED } from "../../pages/Teams/flag";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { HELP_ASSISTANT_ENABLED } from "../HelpAssistant/constants";
import useHelpFabDismissed from "../HelpAssistant/useHelpFabDismissed";
import { setFabDismissed } from "../HelpAssistant/fabPersistence";
import {
  REFERRAL_PROGRAM_SUSPENDED,
  GALLERY_THEMING_ENABLED,
} from "../../constants/featureFlags";
import earningsMenuLabel from "./earningsMenuLabel";

/**
 * The creator EARNINGS / referral program is SUSPENDED (Gordon, 2026-07-24, observed
 * live during a QA session: "The Earnings Portal popup should not be showing up for
 * now - we suspended the earnings program").
 *
 * While this is true the avatar menu never surfaces "Earnings Portal" — the entry
 * reads "Analytics" for every user, including those still flagged as in the referral
 * program.
 *
 * The switch itself now lives in constants/featureFlags as the ONE shared
 * REFERRAL_PROGRAM_SUSPENDED primitive (G-118) — it used to be a const local to this
 * file, which meant it governed this dropdown entry and nothing else while a dozen
 * other surfaces kept advertising the suspended program. Import it; never re-declare
 * a second referral switch here.
 *
 * TO RESUME THE PROGRAM: REACT_APP_REFERRAL_PROGRAM=true. The underlying referral
 * membership (`isInReferralProgram`) is untouched, so nothing is lost while suspended.
 */
const EARNINGS_PROGRAM_SUSPENDED = REFERRAL_PROGRAM_SUSPENDED;

const ProfileMenu = React.forwardRef(
  (
    {
      setModalOpen = null,
      setOnLeave = null,
      onClickBeta = () => {},
      className,
      isJoyrideOpen = false,
      wheelUpdatedDataSorted = [],
      isModalOpen = false, // New prop
    },
    ref
  ) => {
    const { userProfile } = useSelector((state) => state.userProfile);
    const hasUnsavedChanges = useSelector(getHasUnsavedChanges);
    const [showDropdown, setShowDropdown] = useState(false);
    // gordon-0917-themes-popup-add-business-box-and-linkvault: local state
    // stays the source of truth for THIS row's own "Themes" click (unchanged
    // from before -- every existing test that fully mocks react-redux with a
    // static state object still works, since nothing here depends on redux
    // actually re-rendering). Business Box / LinkVault kebabs on the Gallery
    // page live in a SIBLING tree with no direct handle on this component, so
    // they signal through redux (redux/reducers/gallery.js themesPopup)
    // instead; the effect below is a one-way sync FROM that redux signal
    // INTO local state whenever it fires a fresh open.
    const [themesPopupOpen, setThemesPopupOpen] = useState(false);
    const [themesPopupSection, setThemesPopupSection] = useState("gallery");
    const themesPopupSignal = useSelector(selectThemesPopup);
    useEffect(() => {
      if (themesPopupSignal?.open) {
        setThemesPopupSection(themesPopupSignal.section || "gallery");
        setThemesPopupOpen(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themesPopupSignal?.open, themesPopupSignal?.section]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [clicked, setClicked] = useState(false);
    const [, setisLoggingOut] = useState(false);
    const location = useLocation();

    // nem2-sweep-view-wheel-guest: ProfileMenu mounts unconditionally in
    // WheelHeader (View Wheel + Builder), including for logged-out visitors.
    // The dropdown row this feeds only ever renders in the logged-in branch
    // below, so an anonymous visitor firing this authenticated-only
    // /subscription/permissions/ probe was pure waste -- measured as a 401
    // console error on every guest View Wheel page load. explorePage gates
    // it on isUserLoggedIn(); a real logged-in user is unaffected.
    const { isInReferralProgram } = useIfUserEligible(
      FEATURES.REFFERAL_PROGRAM,
      { explorePage: true }
    );
    let nav = useNavigate();
    const dispatch = useDispatch();
    let userLogout = useLogout();
    const dropdownRef = useRef();
    const queryClient = useQueryClient();

    // D-01: the 0-Wheels avatar is a generic iYuga Wheel (six equal brand
    // segments) rendered by the real WheelComponent — not a plain avatar.
    const genericWheelSegments = useMemo(() => getGenericWheelSegments(), []);

    // kelly-1005-loggedout-header-shows-last-user-portrait: `userProfile` is a
    // persisted redux slice (redux/index.js persistConfig has no blacklist
    // entry for it) that survives in localStorage["persist:root"] whenever the
    // LOGOUT action never fires — a silently expired token, a manually cleared
    // keyCloak key, or any other session end that skips ProfileMenu's own
    // handleLogout. EW-1004 purges persist:root on an EXPLICIT Logout click,
    // but a stale persisted userProfile from a still-valid-looking localStorage
    // blob rehydrates into the store on the very next page load regardless —
    // MEASURED live on web-dev.iyuga.net: a browser with a stale persisted
    // profile and no keyCloak token still painted the previous account's real
    // portrait in this header avatar while the dropdown correctly showed
    // "Sign in" (i.e. only the menu CONTENTS were auth-gated, never the image
    // driving the avatar itself). Routing the avatar's image source through
    // isUserLoggedIn() closes that gap without touching the persistence layer.
    const loggedIn = isUserLoggedIn();
    const avatarProfileImage = loggedIn ? userProfile?.profile_image : null;

    // Gordon 2026-07-30 (asked three times): the floating Help launcher is
    // dismissable, and THIS menu is the only way back. The entry appears ONLY
    // while the FAB is hidden for this user — a permanently-present "Help" item
    // that does nothing when the FAB is already on screen would be a dead
    // control (G-54). Subscribed, not read once: dismissing the FAB is a
    // sibling component's write, and this menu must see it without a reload.
    const helpFabUserId = getLoggedInUser()?.id ?? null;
    const helpFabDismissed = useHelpFabDismissed(helpFabUserId);

    const handleLogout = () => {
      // fix-kelly-0930-P0: capture the signing-out account's id BEFORE
      // keyCloak is cleared below -- removeAccount() needs it to forget
      // exactly this account from the switcher registry. Without this, the
      // just-logged-out account stayed listed as a "remembered" account
      // (contradicting accountRegistry.js's own stated guarantee: "A
      // signed-out account leaves no residue in this store") and a
      // subsequent signup on the same browser could list it alongside a
      // brand-new account that never should have known about it.
      const loggedOutId = getLoggedInUser()?.id;
      setisLoggingOut(true);
      userLogout.mutate(null, {
        onSuccess: (response) => {
          setisLoggingOut(false);
          dispatch(logout());
          dispatch(resetGallery());
          dispatch(clearGalleryPopup());
          dispatch(clearAllAiWheels());
          if (loggedOutId != null) {
            removeAccount(loggedOutId);
          }
          localStorage.removeItem("keyCloak");
          sessionStorage.removeItem("referralData");
          purgePersistedState(); // EW-1004: clear persist:root remnant
          queryClient.clear();
          nav("/login");
        },
        onError: (err) => {
          setisLoggingOut(false);
        },
      });
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          // If a click is outside the dropdown
          if (isModalOpen || isJoyrideOpen) {
            // If a modal is open or Joyride step active, do NOT close the dropdown
            return;
          }
          // If click target is one of the Joyride upgrade links, ignore
          const ignoreIds = [
            "premium-link",
            "prosuite-link",
            "upgrading-link",
            "premium-earnings-link",
            "prosuite-earnings-link",
          ];
          if (ignoreIds.includes(event.target.id)) {
            return;
          }
          // Otherwise, close the dropdown
          setShowDropdown(false);
          setClicked(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [dropdownRef, isModalOpen, isJoyrideOpen]);

    useEffect(() => {
      if (!showDropdown) {
        setActiveIndex(-1);
      } else if (isJoyrideOpen) {
        setActiveIndex(1);
      }
    }, [showDropdown, isJoyrideOpen]);

    const handleDropdownItemClick = (action) => {
      if (hasUnsavedChanges && setOnLeave && setModalOpen) {
        setOnLeave(() => action);
        setModalOpen(true);
        setShowDropdown(false);
      } else {
        action();
        setShowDropdown(false);
      }
    };

    React.useImperativeHandle(ref, () => ({
      toggleDropdown: () => {
        setClicked(true);
        setShowDropdown(!showDropdown);
      },
      closeDropdown: () => {
        setClicked(false);
        setShowDropdown(false);
      },
    }));

    return (
      <Styled.UserContainer
        clicked={clicked}
        ref={dropdownRef}
        className={className}
        // A7 (a11y): the avatar is the primary account/navigation menu trigger
        // (Gallery, Settings, Discover, Teams, Logout…). It must be reachable and
        // operable by keyboard and announced to screen readers, not a click-only
        // <div>. One handler for pointer + keyboard so the paths can't diverge.
        role="button"
        tabIndex={0}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={showDropdown}
        onClick={() => {
          setClicked(true);
          setShowDropdown(!showDropdown);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setClicked(true);
            setShowDropdown((prev) => !prev);
          }
        }}
      >
        {" "}
        {wheelUpdatedDataSorted && wheelUpdatedDataSorted?.length > 0 ? (
          <WheelComponent
            userData={{
              // GENERIC_WHEEL_USER_ID is reserved for the 0-Wheels arm below
              // -- this branch has real chart data, so it must never fall
              // back to that sentinel even when getLoggedInUser() is null.
              id: getLoggedInUser()?.id,
              url: avatarProfileImage || ASSETS.iyugaLogo,
            }}
            maxSelectedArcs={0}
            arcThickness={0}
            chartData={wheelUpdatedDataSorted}
            margin={1}
            // Match the wheel's internal geometry to the clamped 46px avatar.
            // Without this the decorative portrait-background circle sizes off
            // the default width="40vw" (calc(40vw*0.6)=24vw≈288px absolute),
            // escaping the 46px avatar and forcing a page-wide horizontal
            // scrollbar on the gallery.
            width="46px"
            setChartData={() => {}}
            containerDivStyle={{
              width: "100%",
              height: "100%",
              maxWidth: "46px",
              maxHeight: "46px",
              marginRight: 10,
              // QA G5-01 (ISP-4018): the WheelComponent renders an internal
              // ~288px absolute layer at its natural size; in this 46px avatar
              // box it escaped the container and pushed the page's horizontal
              // scrollWidth ~300-385px past the viewport on desktop widths,
              // producing a persistent gallery-wide horizontal scrollbar.
              // Clipping the avatar box contains that layer (avatar renders
              // unchanged at 46px). Measured live: overflow 308->0 @1200.
              overflow: "hidden",
            }}
            config={{
              showTooltips: false,
            }}
          />
        ) : (
          // D-01: 0 Wheels -> generic iYuga Wheel (six equal brand segments)
          // via the real renderer, profile image / logo in the center (no hollow
          // center). Geometry matches the has-Wheels 46px avatar frame above.
          <WheelComponent
            userData={{
              id: GENERIC_WHEEL_USER_ID,
              url: avatarProfileImage || ASSETS.iyugaLogo,
            }}
            maxSelectedArcs={0}
            arcThickness={0}
            chartData={genericWheelSegments}
            margin={1}
            width="46px"
            setChartData={() => {}}
            containerDivStyle={{
              width: "100%",
              height: "100%",
              maxWidth: "46px",
              maxHeight: "46px",
              marginRight: 10,
              overflow: "hidden",
            }}
            config={{
              showTooltips: false,
            }}
          />
        )}
        {showDropdown ? (
          !isUserLoggedIn() ? (
            <>
              <Styled.Dropdown>
                <Styled.DropdownItem
                  onClick={() => handleDropdownItemClick(() => nav("/login"))}
                >
                  <LoginIcon style={{ fontSize: "15px", marginRight: "5px" }} />
                  Sign in
                </Styled.DropdownItem>
                <Styled.DropdownItem
                  onClick={
                    () => nav("/signup")
                    // handleDropdownItemClick(onClickBeta)
                  }
                >
                  <AccountBoxIcon
                    style={{
                      fontSize: "18px",
                      marginTop: "1px",
                      marginRight: "5px",
                    }}
                  />
                  {/* kelly-1005-loggedout-header-shows-last-user-portrait (2):
                      Gordon 2026-10-03, "no beta gate" — signup is open now, so
                      the beta-specific copy is stale. */}
                  Sign up
                </Styled.DropdownItem>
              </Styled.Dropdown>
            </>
          ) : (
            <Styled.Dropdown>
              {/* Gordon 2026-07-20: GitHub-style account switcher at the TOP of the
                  menu (a distinct hunk from PR #2090's linked-wheels section near
                  the Logout divider, so the two compose without colliding).
                  Self-gates on REACT_APP_ACCOUNT_SWITCHER; when OFF nothing renders
                  and the account registry is never written. */}
              {ACCOUNT_SWITCHER_ENABLED && (
                <AccountSwitcherSection
                  currentUserId={getLoggedInUser()?.id}
                  keyCloak={{
                    token: getLoggedInUserToken(),
                    user: getLoggedInUser(),
                  }}
                  userProfile={userProfile}
                  onRequestClose={() => {
                    setShowDropdown(false);
                    setClicked(false);
                  }}
                />
              )}
              {location.pathname !== "/gallery" && (
                <Styled.DropdownItem
                  onClick={() =>
                    handleDropdownItemClick(() =>
                      nav(`/gallery/${getLoggedInUser().id}`)
                    )
                  }
                >
                  {/* Gordon 2026-08-18: the Gallery is the user's home base,
                      so this row's icon is the line-art Home/House glyph
                      (matching the header's HomeNavEntry / ISP-9999), replacing
                      the old grid/gallery glyph. Label stays "Gallery". */}
                  <span
                    style={{
                      display: "inline-flex",
                      marginRight: 8,
                      marginLeft: -2,
                    }}
                  >
                    <HouseLineArt color="#667085" width={18} height={18} />
                  </span>
                  <TextView>Gallery</TextView>
                </Styled.DropdownItem>
              )}
              {/* QA-112 follow-up (Gordon 2026-08-12): "Themes" INVERTED --
                  was an unclickable "THEMES" group header + a separate
                  clickable "Gallery Theme" row that navigated away to
                  `?theme=gallery`. Now "Themes" itself is the ONE clickable
                  row (same style as Gallery/Media Library/Discover/Settings)
                  that opens the ThemesPopup in place, no navigation. Gallery
                  Theme is now a SECTION inside that popup (see
                  ThemesPopup.jsx) -- the future home for Business Box +
                  LinkVault theme sections too. Same flag-gate the old item
                  used, so this stays byte-identical when
                  GALLERY_THEMING_ENABLED is OFF. */}
              {GALLERY_THEMING_ENABLED && (
                <Styled.DropdownItem
                  onClick={() => {
                    // Opens a popup in place -- not a navigation -- so the
                    // hasUnsavedChanges "leave this page?" gate (which the
                    // other rows funnel through via handleDropdownItemClick)
                    // does not apply here.
                    setShowDropdown(false);
                    setClicked(false);
                    setThemesPopupSection("gallery");
                    setThemesPopupOpen(true);
                  }}
                >
                  <PaletteOutlinedIcon
                    aria-hidden="true"
                    style={{ fontSize: 18, marginRight: 8, marginLeft: -2, color: "#667085" }}
                  />
                  <TextView>Themes</TextView>
                </Styled.DropdownItem>
              )}
              <Styled.DropdownItem
                onClick={() =>
                  handleDropdownItemClick(() => nav(PATH.MEDIA_LIBRARY))
                }
              >
                <Styled.DropdownImage
                  src={ASSETS.mediaSidebarIcon || grid}
                  alt="Media Library"
                />
                <TextView>Media Library</TextView>
              </Styled.DropdownItem>
              {/* QA-151 (Kelly, 2026-08-19): "Discover" removed from THIS menu
                  only — the /discover route and its other nav entry points
                  (header, Ctrl+K) are untouched. See frame-03/04 vs the new
                  order spec in C:\iYuga\qa-notes\media\QA-151. */}
              <Styled.DropdownItem
                className="earnings-portal-dropdown-item"
                isActive={activeIndex === 1}
                onClick={() => handleDropdownItemClick(() => nav("/referrals"))}
              >
                <Styled.DropdownImage2
                  src={ASSETS.Office}
                  alt="earingsportal"
                />{" "}
                {/* Gordon 2026-07-24 (observed live during QA): "The Earnings Portal
                    popup should not be showing up for now - we suspended the earnings
                    program." While EARNINGS_PROGRAM_SUSPENDED is true this entry always
                    reads "Analytics" and never surfaces the Earnings Portal, regardless
                    of the user's referral-program membership. Flip the constant back to
                    false to restore the previous behaviour when the program resumes —
                    the referral state itself is untouched, so nothing is lost. */}
                <TextView>
                  {earningsMenuLabel({
                    suspended: EARNINGS_PROGRAM_SUSPENDED,
                    isInReferralProgram,
                  })}
                </TextView>
              </Styled.DropdownItem>

              {/* fix-gordon-0928-P1 (Gordon 2026-09-28: "Responses should be
                  part of Analytics, not a separate tab"): the standalone
                  Responses row (gordon-0923-P1-responses-section-forms-polls-
                  locks-tabbed) is removed — Responses now lives as a tab on
                  /referrals, reachable via the "Analytics" row directly above. */}

              {/* QA-151 (Kelly, 2026-08-19) superseded 2026-09-23 (Gordon,
                  "re-enable Teams on Dev"): this row is now ACTIVE wherever
                  TEAMS_ENABLED is on (Development/Sandbox today) -- a real
                  "Teams" link to /teams -- and stays a disabled "Teams
                  (Coming Soon)" row everywhere else (Staging/Production),
                  same row, same icon, gated on the single TEAMS_ENABLED
                  build flag. */}
              {TEAMS_ENABLED ? (
                <Styled.DropdownItem
                  role="menuitem"
                  onClick={() =>
                    handleDropdownItemClick(() => nav("/teams"))
                  }
                >
                  <WorkspacesOutlinedIcon
                    style={{
                      fontSize: "18px",
                      marginRight: "8px",
                      marginLeft: "-2px",
                    }}
                  />
                  <TextView>Teams</TextView>
                </Styled.DropdownItem>
              ) : (
                <Styled.DropdownItem
                  aria-disabled="true"
                  aria-label="Teams (Coming Soon) — not yet available"
                  role="menuitem"
                  tabIndex={-1}
                  onClick={(e) => e.preventDefault()}
                  style={{ opacity: 0.5, cursor: "not-allowed" }}
                >
                  <WorkspacesOutlinedIcon
                    style={{
                      fontSize: "18px",
                      marginRight: "8px",
                      marginLeft: "-2px",
                    }}
                  />
                  <TextView>Teams (Coming Soon)</TextView>
                </Styled.DropdownItem>
              )}
              <Styled.DropdownItem
                onClick={() => handleDropdownItemClick(() => nav("/settings"))}
              >
                <Styled.DropdownImage src={ASSETS.settings} alt="Settings" />
                <TextView>Settings</TextView>
              </Styled.DropdownItem>

              {/* Gordon 2026-07-30 (x3): recovery path for a dismissed Help
                  launcher. Rendered only while the FAB is actually hidden, so
                  the menu never carries an entry that does nothing. Clicking it
                  clears the per-user dismissed flag; the FAB re-mounts at its
                  persisted position immediately (same subscribed store). */}
              {HELP_ASSISTANT_ENABLED && helpFabDismissed && (
                <Styled.DropdownItem
                  onClick={() =>
                    handleDropdownItemClick(() =>
                      setFabDismissed(helpFabUserId, false)
                    )
                  }
                >
                  <HelpOutlineIcon
                    style={{
                      fontSize: "18px",
                      marginRight: "8px",
                      marginLeft: "-2px",
                    }}
                  />
                  <TextView>Help</TextView>
                </Styled.DropdownItem>
              )}

              {/* Gordon 2026-07-20: the signed-in user's wheels that are linked to
                  each other, grouped, each navigating to that wheel. Self-gates on
                  REACT_APP_LINKED_WHEELS_ENABLED and hides when there are none, so
                  the menu never gains a dead/empty section. */}
              <AvatarLinkedWheelsSection
                userId={getLoggedInUser()?.id}
                onNavigate={(wheelId) =>
                  handleDropdownItemClick(() => nav(`/wheel/${wheelId}`))
                }
              />

              <Styled.Divider />
              <Styled.DropdownItem
                onClick={() => handleDropdownItemClick(handleLogout)}
              >
                <Styled.DropdownImage src={log_out} alt="Logout" />{" "}
                <TextView>Logout</TextView>
              </Styled.DropdownItem>
            </Styled.Dropdown>
          )
        ) : null}
        {/* Rendered as a sibling of the dropdown (not inside the showDropdown
            ternary above) -- the popup must stay open after the dropdown
            itself closes on click, per the "open in place" behaviour. */}
        {GALLERY_THEMING_ENABLED && (
          <ThemesPopup
            open={themesPopupOpen}
            onClose={() => {
              setThemesPopupOpen(false);
              // Reset the redux signal too, so a second click on a Business
              // Box / LinkVault kebab (same section as last time) still
              // transitions open:false -> true and re-fires the sync effect.
              dispatch(closeThemesPopup());
            }}
            userProfile={userProfile}
            initialSection={themesPopupSection}
          />
        )}
      </Styled.UserContainer>
    );
  }
);

export default ProfileMenu;
